import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { DeletionPolicyService } from '../../../../services/entity-services/deletion-policy.service';
import { ResourcePolicyResponse, VMPolicy, SubscriptionPolicy, PolicyOption, NotificationIntimation, Team } from '../../../../interfaces/resource-policy.interface';
import { SidebarNavMenuItem } from '../../../../decorators/sidebar-nav-menu-item';
import { NotificationService } from '../../../../services/notification.service';
import { Log, IShortLogger } from '../../../../services/logger.service';
import { BusinessProcessEntityService } from '../../../../services/settings/businessprocess-entity.service';


@Component({
  selector: 'app-deletion-policy',
  templateUrl: './deletion-policy.component.html',
  styleUrl: './deletion-policy.component.css'
})
export class DeletionPolicyComponent implements OnInit {
  private readonly logger: IShortLogger<DeletionPolicyComponent> = Log.create(DeletionPolicyComponent);

  @Output() policyCreated = new EventEmitter<void>();
  @Input() resourceType: 'vm' | 'subscription' | 'both' = 'both'; // Control which tabs to show
  @Input() defaultTab: 'vm' | 'subscription' = 'vm'; // Set default active tab

  resourcePolicy: ResourcePolicyResponse | null = null;
  policyTemplates: any[] = []; // Store policy templates from service
  loading = false;
  error: string | null = null;
  activeTab: 'vm' | 'subscription' = 'vm';
  availableTeams: any[] = []; // Loaded from service

  // Policy name and description fields
  vmPolicyName: string = '';
  vmPolicyDescription: string = '';
  subscriptionPolicyName: string = '';
  subscriptionPolicyDescription: string = '';

  // Expand/collapse states
  expandedSections: { [key: string]: boolean } = {
    'vm-creation': true,
    'vm-daily': true,
    'vm-classend': true,
    'subscription-creation': true,
    'subscription-daily': true,
    'subscription-classend': true,
    'subscription-quota': true,
    'subscription-notifications': true
  };

  constructor(
    private deletionPolicyService: DeletionPolicyService,
    private businessProcessEntityService: BusinessProcessEntityService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    // Set the active tab based on input
    if (this.resourceType === 'vm') {
      this.activeTab = 'vm';
    } else if (this.resourceType === 'subscription') {
      this.activeTab = 'subscription';
    } else {
      this.activeTab = this.defaultTab;
    }
    this.loadTeams();

    // Load policy templates from service instead of hardcoded data
    this.loadPolicyTemplates();
  }
  loadTeams(): void {
    this.loading = true;
    this.error = null;

    // For demo purposes, using sample data
    // Replace this with actual API call when ready:
    this.businessProcessEntityService.getAllTeams().subscribe({
      next: (result) => {
        // Filter out participants from available teams
        this.availableTeams = result.filter((team: any) => {
          const teamName = (team.Name || team.name || '').toLowerCase();
          return teamName !== 'participant' && teamName !== 'participants';
        });
        this.logger.i('Loaded Team (excluding participants):', this.availableTeams);

        // Reload policy templates after teams are loaded to populate NotificationIntimations correctly
        if (this.availableTeams.length > 0) {
          this.loadPolicyTemplates();
        }
        this.loading = false;
      },
      error: (err) => {
        this.logger.e('Error loading Teams:', err);
        this.loading = false;
      }
    });

  }
  createTemplatePolicy(): void {
    // Initialize policy names and descriptions as blank by default
    this.vmPolicyName = '';
    this.vmPolicyDescription = '';
    this.subscriptionPolicyName = '';
    this.subscriptionPolicyDescription = '';

    // Create a template policy structure with the new data format
    this.resourcePolicy = {
      _id: { $oid: 'template' },
      VMPolicy: {
        CreationPolicyOptions: [
          {
            optionName: 'createAtLaunchTime',
            optionValue: 'true',
            description: 'Let the VM resource be created at first launch time'
          }
        ],
        DailyDeletionPolicyOptions: [
          {
            optionName: 'manual',
            optionValue: 'false',
            description: "Don't delete any resource / will do manually"
          },
          {
            optionName: 'shutdown',
            optionValue: 'false',
            description: 'Gracefully shut down the guest OS #parVal #parUnit after session (VM stays allocated, charges continue)',
            parVal: '30',
            parUnit: 'minutes'
          },
          {
            optionName: 'deallocate',
            optionValue: 'false',
            description: 'Stop and deallocate the VM #parVal #parUnit after session (compute charges paused, no OS signal)',
            parVal: '30',
            parUnit: 'minutes'
          },
          {
            optionName: 'shutdownAndDeallocate',
            optionValue: 'false',
            description: 'Gracefully shut down then deallocate the VM #parVal #parUnit after session (compute charges paused)',
            parVal: '30',
            parUnit: 'minutes'
          },
          {
            optionName: 'deleteResources',
            optionValue: 'true',
            description: 'Delete following resources every day #parVal #parUnit after session',
            parVal: '30',
            parUnit: 'minutes',
            resourcesToBeDeleted: ['vm', 'nsg', 'subnet', 'publicIP'],
            allResources: ['os_disk', 'vm', 'nsg', 'subnet', 'publicIP']
          }
        ],
        ClassEndDeletionOptions: [
          {
            optionName: 'manual',
            optionValue: 'false',
            description: "Don't perform any automated action at class end"
          },
          {
            optionName: 'shutdown',
            optionValue: 'false',
            description: 'Gracefully shut down the guest OS after #parVal #parUnit at class end',
            parVal: '1',
            parUnit: 'hours'
          },
          {
            optionName: 'deallocate',
            optionValue: 'false',
            description: 'Stop and deallocate VMs after #parVal #parUnit at class end',
            parVal: '1',
            parUnit: 'hours'
          },
          {
            optionName: 'shutdownAndDeallocate',
            optionValue: 'false',
            description: 'Gracefully shut down then deallocate VMs after #parVal #parUnit at class end',
            parVal: '1',
            parUnit: 'hours'
          },
          {
            optionName: 'deleteResources',
            optionValue: 'true',
            description: 'Delete following resources after #parVal #parUnit',
            parVal: '1',
            parUnit: 'day',
            resourcesToBeDeleted: ['os_disk', 'vm', 'nsg', 'subnet', 'publicIP'],
            allResources: ['os_disk', 'vm', 'nsg', 'subnet', 'publicIP']
          }
        ]
      },
      SubscriptionPolicy: {
        CreateOptions: [
          {
            optionName: 'createAtLaunchTime',
            optionValue: 'true',
            description: 'Let the subscription user account be created the first time the user logs into the portal and provides credentials'
          }
        ],
        DailyDeletionPolicyOptions: [
          {
            optionName: 'manual',
            optionValue: 'false',
            description: "Don't do any activity"
          },
          {
            optionName: 'deactivateSubscription',
            optionValue: 'false',
            description: 'Deactivate subscription every day #parVal #parUnit after session',
            parVal: '1',
            parUnit: 'min'
          },
          {
            optionName: 'deleteSubsResources',
            optionValue: 'true',
            description: 'Delete all resources in subscription every day #parVal #parUnit after session',
            parVal: '1',
            parUnit: 'min'
          }
        ],
        ClassEndDeletionOptions: [
          {
            optionName: 'deleteSubsResources',
            optionValue: 'true',
            description: 'Delete all resources in subscription after #parVal #parUnit',
            parVal: '1',
            parUnit: 'min'
          }
        ],
        QuotaReached: [
          {
            optionName: 'extendQuota',
            optionValue: 'false',
            description: 'Extend quota limit by #parVal #parUnit',
            parVal: '100',
            parUnit: 'Rs'
          },
          {
            optionName: 'deactivateSubscription',
            optionValue: 'true',
            description: 'Deactivate subscription'
          },
          {
            optionName: 'deleteSubsResources',
            optionValue: 'false',
            description: 'Delete all resources in subscription'
          }
        ],
        NotificationIntimations: [
          {
            percentage: '70',
            teams: []
          },
          {
            percentage: '80',
            teams: []
          },
          {
            percentage: '100',
            teams: []
          }
        ]
      }
    };
    this.processDescriptions();
  }

  loadPolicyTemplates(): void {
    this.loading = true;
    this.error = null;

    this.deletionPolicyService.getAllResourcePolicies().subscribe({
      next: (templates) => {
        this.policyTemplates = templates;
        this.buildPolicyFromTemplates(templates);
        this.loading = false;
      },
      error: (error) => {
        this.error = 'Failed to load policy templates. Please try again.';
        this.loading = false;
        this.logger.e('Error loading policy templates:', error);
        // Fallback to hardcoded template if service fails
        this.createTemplatePolicy();
      }
    });
  }

  buildPolicyFromTemplates(templates: any[]): void {
    this.logger.i('Loading policy templates from service:', templates);

    // Initialize policy names and descriptions as blank by default
    this.vmPolicyName = '';
    this.vmPolicyDescription = '';
    this.subscriptionPolicyName = '';
    this.subscriptionPolicyDescription = '';

    // Find default VM and Subscription policies
    const defaultVMPolicy = templates.find(t => t.ResourceType === 'VM' && t.IsDefault === 'true');
    const defaultSubscriptionPolicy = templates.find(t => t.ResourceType === 'Subscription' && t.IsDefault === 'true');

    this.logger.i('Default VM Policy found:', defaultVMPolicy);
    this.logger.i('Default Subscription Policy found:', defaultSubscriptionPolicy);

    // Build the resource policy structure
    this.resourcePolicy = {
      _id: { $oid: 'template' },
      VMPolicy: defaultVMPolicy ? {
        CreationPolicyOptions: defaultVMPolicy.CreationPolicyOptions || [],
        DailyDeletionPolicyOptions: defaultVMPolicy.DailyDeletionPolicyOptions || [],
        ClassEndDeletionOptions: defaultVMPolicy.ClassEndDeletionOptions || []
      } : this.getDefaultVMPolicy(),
      SubscriptionPolicy: defaultSubscriptionPolicy ? {
        CreateOptions: defaultSubscriptionPolicy.CreateOptions || [],
        DailyDeletionPolicyOptions: defaultSubscriptionPolicy.DailyDeletionPolicyOptions || [],
        ClassEndDeletionOptions: defaultSubscriptionPolicy.ClassEndDeletionOptions || [],
        QuotaReached: defaultSubscriptionPolicy.QuotaReached || [],
        // Clear all teams from NotificationIntimations to ensure no checkboxes are pre-selected
        NotificationIntimations: (defaultSubscriptionPolicy.NotificationIntimations || []).map((notification: any) => ({
          percentage: notification.percentage,
          teams: []
        }))
      } : this.getDefaultSubscriptionPolicy()
    };

    this.logger.i('Built resource policy:', this.resourcePolicy);
    this.processDescriptions();
  }

  getDefaultVMPolicy(): any {
    return {
      CreationPolicyOptions: [
        {
          optionName: 'createAtLaunchTime',
          optionValue: 'true',
          description: 'Let the VM resource be created at first launch time'
        }
      ],
      DailyDeletionPolicyOptions: [
        {
          optionName: 'manual',
          optionValue: 'false',
          description: "Don't delete any resource / will do manually"
        },
        {
          optionName: 'shutdown',
          optionValue: 'false',
          description: 'Gracefully shut down the guest OS #parVal #parUnit after session (VM stays allocated, charges continue)',
          parVal: '30',
          parUnit: 'minutes'
        },
        {
          optionName: 'deallocate',
          optionValue: 'false',
          description: 'Stop and deallocate the VM #parVal #parUnit after session (compute charges paused, no OS signal)',
          parVal: '30',
          parUnit: 'minutes'
        },
        {
          optionName: 'shutdownAndDeallocate',
          optionValue: 'false',
          description: 'Gracefully shut down then deallocate the VM #parVal #parUnit after session (compute charges paused)',
          parVal: '30',
          parUnit: 'minutes'
        },
        {
          optionName: 'deleteResources',
          optionValue: 'true',
          description: 'Delete following resources every day #parVal #parUnit after session',
          parVal: '30',
          parUnit: 'minutes',
          resourcesToBeDeleted: ['vm', 'nsg', 'subnet', 'publicIP'],
          allResources: ['os_disk', 'vm', 'nsg', 'subnet', 'publicIP']
        }
      ],
      ClassEndDeletionOptions: [
        {
          optionName: 'manual',
          optionValue: 'false',
          description: "Don't perform any automated action at class end"
        },
        {
          optionName: 'shutdown',
          optionValue: 'false',
          description: 'Gracefully shut down the guest OS after #parVal #parUnit at class end',
          parVal: '1',
          parUnit: 'hours'
        },
        {
          optionName: 'deallocate',
          optionValue: 'false',
          description: 'Stop and deallocate VMs after #parVal #parUnit at class end',
          parVal: '1',
          parUnit: 'hours'
        },
        {
          optionName: 'shutdownAndDeallocate',
          optionValue: 'false',
          description: 'Gracefully shut down then deallocate VMs after #parVal #parUnit at class end',
          parVal: '1',
          parUnit: 'hours'
        },
        {
          optionName: 'deleteResources',
          optionValue: 'true',
          description: 'Delete following resources after #parVal #parUnit',
          parVal: '1',
          parUnit: 'day',
          resourcesToBeDeleted: ['os_disk', 'vm', 'nsg', 'subnet', 'publicIP'],
          allResources: ['os_disk', 'vm', 'nsg', 'subnet', 'publicIP']
        }
      ]
    };
  }

  getDefaultSubscriptionPolicy(): any {
    // Get the first team from availableTeams if available, otherwise use empty array
    const defaultTeams = this.availableTeams.length > 0
      ? [{ name: this.availableTeams[0].Name || this.availableTeams[0].name, Id: this.availableTeams[0]._id || this.availableTeams[0].Id }]
      : [];

    return {
      CreateOptions: [
        {
          optionName: 'createAtLaunchTime',
          optionValue: 'true',
          description: 'Let the subscription user account be created the first time the user logs into the portal and provides credentials'
        }
      ],
      DailyDeletionPolicyOptions: [
        {
          optionName: 'manual',
          optionValue: 'false',
          description: "Don't do any activity"
        },
        {
          optionName: 'deactivateSubscription',
          optionValue: 'false',
          description: 'Deactivate subscription every day #parVal #parUnit after session',
          parVal: '1',
          parUnit: 'min'
        },
        {
          optionName: 'deleteSubsResources',
          optionValue: 'true',
          description: 'Delete all resources in subscription every day #parVal #parUnit after session',
          parVal: '1',
          parUnit: 'min'
        }
      ],
      ClassEndDeletionOptions: [
        {
          optionName: 'deleteSubsResources',
          optionValue: 'true',
          description: 'Delete all resources in subscription after #parVal #parUnit',
          parVal: '1',
          parUnit: 'min'
        }
      ],
      QuotaReached: [
        {
          optionName: 'extendQuota',
          optionValue: 'false',
          description: 'Extend quota limit by #parVal #parUnit',
          parVal: '100',
          parUnit: 'Rs'
        },
        {
          optionName: 'deactivateSubscription',
          optionValue: 'true',
          description: 'Deactivate subscription'
        },
        {
          optionName: 'deleteSubsResources',
          optionValue: 'false',
          description: 'Delete all resources in subscription'
        }
      ],
      NotificationIntimations: [
        {
          percentage: '70',
          teams: []
        },
        {
          percentage: '80',
          teams: []
        },
        {
          percentage: '100',
          teams: []
        }
      ]
    };
  }

  processDescriptions(): void {
    // Process VM Policy descriptions
    if (this.resourcePolicy?.VMPolicy) {
      this.processOptionsDescriptions(this.resourcePolicy.VMPolicy.DailyDeletionPolicyOptions);
      this.processOptionsDescriptions(this.resourcePolicy.VMPolicy.ClassEndDeletionOptions);
    }

    // Process Subscription Policy descriptions
    if (this.resourcePolicy?.SubscriptionPolicy) {
      this.processOptionsDescriptions(this.resourcePolicy.SubscriptionPolicy.DailyDeletionPolicyOptions);
      this.processOptionsDescriptions(this.resourcePolicy.SubscriptionPolicy.ClassEndDeletionOptions);
    }
  }

  processOptionsDescriptions(_options: PolicyOption[]): void {
    // No-op: description formatting is handled in-template via
    // formatDescription() and sanitizeDescription() to avoid permanent mutation.
  }

  /**
   * Returns a description with #parVal / #parUnit tokens replaced by
   * their current values — without mutating the stored description string.
   */
  formatDescription(option: any): string {
    if (!option?.description) return '';
    let desc: string = option.description;
    if (option.parVal != null) desc = desc.replace(/#parVal/g, option.parVal);
    if (option.parUnit != null) desc = desc.replace(/#parUnit/g, option.parUnit);
    return desc;
  }

  /**
   * Returns a description with #parVal / #parUnit placeholder tokens
   * stripped out — used for options whose timing is shown via dedicated
   * input fields rather than inline in the label.
   */
  sanitizeDescription(option: any): string {
    if (!option?.description) return '';
    return option.description
      .replace(/#parVal(\s*#parUnit)?/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  /** Index of the currently active tab for mat-tab-group. */
  get activeTabIndex(): number {
    if (this.resourceType === 'subscription') return 0;
    return this.activeTab === 'subscription' ? 1 : 0;
  }

  onTabChange(index: number): void {
    if (this.resourceType === 'both') {
      this.activeTab = index === 0 ? 'vm' : 'subscription';
    }
  }

  replaceParameters(description: string, parVal: string, parUnit: string): string {
    return description.replace(/#parVal/g, parVal).replace(/#parUnit/g, parUnit);
  }

  setActiveTab(tab: 'vm' | 'subscription'): void {
    this.activeTab = tab;
  }

  onPolicyOptionChange(policyType: 'vm' | 'subscription', section: string, selectedOption: PolicyOption): void {
    // Handle radio button behavior - only one option can be selected per section
    if (policyType === 'vm' && this.resourcePolicy?.VMPolicy) {
      if (section === 'creation') {
        this.resourcePolicy.VMPolicy.CreationPolicyOptions.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      } else if (section === 'daily') {
        this.resourcePolicy.VMPolicy.DailyDeletionPolicyOptions.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      } else if (section === 'classend') {
        this.resourcePolicy.VMPolicy.ClassEndDeletionOptions.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      }
    } else if (policyType === 'subscription' && this.resourcePolicy?.SubscriptionPolicy) {
      if (section === 'creation') {
        this.resourcePolicy.SubscriptionPolicy.CreateOptions.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      } else if (section === 'daily') {
        this.resourcePolicy.SubscriptionPolicy.DailyDeletionPolicyOptions.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      } else if (section === 'classend') {
        this.resourcePolicy.SubscriptionPolicy.ClassEndDeletionOptions.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      } else if (section === 'quota') {
        this.resourcePolicy.SubscriptionPolicy.QuotaReached?.forEach(option => {
          option.optionValue = option === selectedOption ? 'true' : 'false';
        });
      }
    }

    this.logger.i('Policy option changed:', { policyType, section, selectedOption });
  }

  onResourceSelectionChange(policyType: 'vm' | 'subscription', section: string, option: PolicyOption, resource: string, selected: boolean): void {
    if (!option.resourcesToBeDeleted) {
      option.resourcesToBeDeleted = [];
    }

    if (selected && !option.resourcesToBeDeleted.includes(resource)) {
      option.resourcesToBeDeleted.push(resource);
    } else if (!selected) {
      option.resourcesToBeDeleted = option.resourcesToBeDeleted.filter(r => r !== resource);
    }
  }

  onParameterChange(option: PolicyOption): void {
    // Update description with new parameter values
    if (option.parVal && option.parUnit) {
      // Get the original description template and replace parameters
      const originalDescription = option.description;

      // If the description already has replaced values, we need to restore the template
      // For now, we'll reconstruct common templates
      let template = '';
      if (originalDescription.includes('Deactivate subscription every day')) {
        template = 'Deactivate subscription every day #parVal #parUnit after session';
      } else if (originalDescription.includes('Delete all resources in subscription every day')) {
        template = 'Delete all resources in subscription every day #parVal #parUnit after session';
      } else if (originalDescription.includes('Delete all resources in subscription after')) {
        template = 'Delete all resources in subscription after #parVal #parUnit';
      } else if (originalDescription.includes('Delete following resources every day')) {
        template = 'Delete following resources every day #parVal #parUnit after session';
      } else if (originalDescription.includes('Delete following resources after')) {
        template = 'Delete following resources after #parVal #parUnit';
      } else {
        template = originalDescription;
      }

      option.description = this.replaceParameters(template, option.parVal, option.parUnit);
    }
  }

  savePolicy(): void {
    if (!this.resourcePolicy) return;

    // Validate both policies before saving
    let validationPassed = true;

    if (this.resourcePolicy.VMPolicy && this.activeTab === 'vm') {
      validationPassed = this.validateVMPolicy();
    } else if (this.resourcePolicy.SubscriptionPolicy && this.activeTab === 'subscription') {
      validationPassed = this.validateSubscriptionPolicy();
    } else {
      // If saving both, validate both
      if (this.resourcePolicy.VMPolicy) {
        validationPassed = this.validateVMPolicy();
      }
      if (validationPassed && this.resourcePolicy.SubscriptionPolicy) {
        validationPassed = this.validateSubscriptionPolicy();
      }
    }

    if (!validationPassed) {
      return;
    }

    this.loading = true;

    // Save both VM and Subscription policies
    const promises: Promise<any>[] = [];

    // Create VM policy request
    if (this.resourcePolicy.VMPolicy) {
      const vmRequest = this.transformToVMApiFormat();
      promises.push(this.deletionPolicyService.createResourcePolicy(vmRequest).toPromise());
    }

    // Create Subscription policy request
    if (this.resourcePolicy.SubscriptionPolicy) {
      const subscriptionRequest = this.transformToSubscriptionApiFormat();
      promises.push(this.deletionPolicyService.createResourcePolicy(subscriptionRequest).toPromise());
    }

    // Execute all requests
    Promise.all(promises).then(
      (responses) => {
        this.loading = false;
        this.logger.i('All policies saved successfully:', responses);
        this.notification.notify('All policies saved successfully', 'success', 3000);
      }
    ).catch(
      (error) => {
        this.error = 'Failed to save one or more policies';
        this.loading = false;
        this.logger.e('Error saving policies:', error);
        this.notification.notify('Failed to save one or more policies', 'error', 3000);
      }
    );
  }

  onNotificationTeamChange(percentage: string, teamName: string, checked: boolean): void {
    if (!this.resourcePolicy?.SubscriptionPolicy?.NotificationIntimations) return;

    const notification = this.resourcePolicy.SubscriptionPolicy.NotificationIntimations.find(n => n.percentage === percentage);
    if (!notification) return;

    if (checked) {
      if (!notification.teams.find(t => t.name === teamName)) {
        // Find the team ID from availableTeams
        const teamData = this.availableTeams.find((team: any) => (team.Name || team.name) === teamName);
        const teamId = teamData ? (teamData._id || teamData.Id || '') : '';
        notification.teams.push({ name: teamName, Id: teamId });
      }
    } else {
      const index = notification.teams.findIndex(t => t.name === teamName);
      if (index > -1) {
        notification.teams.splice(index, 1);
      }
    }
  }

  onPercentageChange(index: number, value: string): void {
    if (!this.resourcePolicy?.SubscriptionPolicy?.NotificationIntimations) return;

    let newPercentage = parseInt(value);

    // Validate range 0-100
    if (isNaN(newPercentage) || newPercentage < 0) {
      newPercentage = 0;
    } else if (newPercentage > 100) {
      newPercentage = 100;
    }

    if (this.resourcePolicy.SubscriptionPolicy.NotificationIntimations[index]) {
      this.resourcePolicy.SubscriptionPolicy.NotificationIntimations[index].percentage = newPercentage.toString();
    }
  }

  getAllTeamTypes(): string[] {
    // Return team names from availableTeams loaded from the service
    if (this.availableTeams && this.availableTeams.length > 0) {
      return this.availableTeams.map((team: any) => team.Name || team.name).sort();
    }

    // Fallback to teams in the current policy if availableTeams not loaded yet
    if (!this.resourcePolicy?.SubscriptionPolicy?.NotificationIntimations) return [];

    const teamTypes = new Set<string>();
    this.resourcePolicy.SubscriptionPolicy.NotificationIntimations.forEach(notification => {
      notification.teams.forEach(team => teamTypes.add(team.name));
    });

    return Array.from(teamTypes).sort();
  }

  hasTeamInNotification(percentage: string, teamName: string): boolean {
    if (!this.resourcePolicy?.SubscriptionPolicy?.NotificationIntimations) return false;

    const notification = this.resourcePolicy.SubscriptionPolicy.NotificationIntimations.find(n => n.percentage === percentage);
    return notification ? notification.teams.some(t => t.name === teamName) : false;
  }

  saveVMPolicy(): void {
    if (!this.resourcePolicy?.VMPolicy) return;

    // Validate VM policy fields
    if (!this.validateVMPolicy()) {
      return;
    }

    this.loading = true;

    // Transform data to match API format
    const vmPolicyRequest = this.transformToVMApiFormat();

    this.deletionPolicyService.createResourcePolicy(vmPolicyRequest).subscribe({
      next: (response) => {
        this.loading = false;
        this.logger.i('VM Policy saved successfully:', response);
        // Show success message
        this.notification.notify('VM Policy saved successfully', 'success', 3000);
        // Emit event to parent component to close modal and refresh list
        this.policyCreated.emit();
      },
      error: (err) => {
        this.error = 'Failed to save VM policy';
        this.loading = false;
        this.logger.e('Error saving VM policy:', err);
        this.notification.notify('Failed to save VM policy', 'error', 3000);
      }
    });
  }

  saveSubscriptionPolicy(): void {
    if (!this.resourcePolicy?.SubscriptionPolicy) return;

    // Validate subscription policy fields
    if (!this.validateSubscriptionPolicy()) {
      return;
    }

    this.loading = true;

    // Transform data to match API format
    const subscriptionPolicyRequest = this.transformToSubscriptionApiFormat();

    this.deletionPolicyService.createResourcePolicy(subscriptionPolicyRequest).subscribe({
      next: (response) => {
        this.loading = false;
        this.logger.i('Subscription Policy saved successfully:', response);
        // Show success message
        this.notification.notify('Subscription Policy saved successfully', 'success', 3000);
        // Emit event to parent component to close modal and refresh list
        this.policyCreated.emit();
      },
      error: (err) => {
        this.error = 'Failed to save subscription policy';
        this.loading = false;
        this.logger.e('Error saving subscription policy:', err);
        this.notification.notify('Failed to save subscription policy', 'error', 3000);
      }
    });
  }

  // Validation method for VM policy
  private validateVMPolicy(): boolean {
    if (!this.vmPolicyName || this.vmPolicyName.trim() === '') {
      this.notification.notify('VM Policy Name is required', 'error', 3000);
      return false;
    }

    if (this.vmPolicyName.trim().length < 3) {
      this.notification.notify('VM Policy Name must be at least 3 characters long', 'error', 3000);
      return false;
    }

    if (this.vmPolicyName.trim().length > 100) {
      this.notification.notify('VM Policy Name cannot exceed 100 characters', 'error', 3000);
      return false;
    }

    if (this.vmPolicyDescription && this.vmPolicyDescription.trim().length > 500) {
      this.notification.notify('VM Policy Description cannot exceed 500 characters', 'error', 3000);
      return false;
    }

    return true;
  }

  // Validation method for subscription policy
  private validateSubscriptionPolicy(): boolean {
    if (!this.subscriptionPolicyName || this.subscriptionPolicyName.trim() === '') {
      this.notification.notify('Subscription Policy Name is required', 'error', 3000);
      return false;
    }

    if (this.subscriptionPolicyName.trim().length < 3) {
      this.notification.notify('Subscription Policy Name must be at least 3 characters long', 'error', 3000);
      return false;
    }

    if (this.subscriptionPolicyName.trim().length > 100) {
      this.notification.notify('Subscription Policy Name cannot exceed 100 characters', 'error', 3000);
      return false;
    }

    if (this.subscriptionPolicyDescription && this.subscriptionPolicyDescription.trim().length > 500) {
      this.notification.notify('Subscription Policy Description cannot exceed 500 characters', 'error', 3000);
      return false;
    }

    return true;
  }

  // Transform VM policy to API format
  private transformToVMApiFormat(): any {
    const vmPolicy = this.resourcePolicy?.VMPolicy;
    if (!vmPolicy) return null;

    return {
      ResourceType: "VM",
      PolicyName: this.vmPolicyName.trim(),
      Description: this.vmPolicyDescription.trim() || "",
      CreationPolicyOptions: vmPolicy.CreationPolicyOptions?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue
      })) || [],
      DailyDeletionPolicyOptions: vmPolicy.DailyDeletionPolicyOptions?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue,
        ...(option.parUnit && { parUnit: option.parUnit }),
        ...(option.parVal && { parVal: option.parVal }),
        ...(option.resourcesToBeDeleted && { resourcesToBeDeleted: option.resourcesToBeDeleted }),
        ...(option.allResources && { allResources: option.allResources })
      })) || [],
      ClassEndDeletionOptions: vmPolicy.ClassEndDeletionOptions?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue,
        ...(option.parUnit && { parUnit: option.parUnit }),
        ...(option.parVal && { parVal: option.parVal }),
        ...(option.resourcesToBeDeleted && { resourcesToBeDeleted: option.resourcesToBeDeleted }),
        ...(option.allResources && { allResources: option.allResources })
      })) || []
    };
  }

  // Transform Subscription policy to API format
  private transformToSubscriptionApiFormat(): any {
    const subscriptionPolicy = this.resourcePolicy?.SubscriptionPolicy;
    if (!subscriptionPolicy) return null;

    return {
      ResourceType: "Subscription",
      PolicyName: this.subscriptionPolicyName.trim(),
      Description: this.subscriptionPolicyDescription.trim() || "",
      CreateOptions: subscriptionPolicy.CreateOptions?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue
      })) || [],
      DailyDeletionPolicyOptions: subscriptionPolicy.DailyDeletionPolicyOptions?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue,
        ...(option.parUnit && { parUnit: option.parUnit }),
        ...(option.parVal && { parVal: option.parVal })
      })) || [],
      ClassEndDeletionOptions: subscriptionPolicy.ClassEndDeletionOptions?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue,
        ...(option.parUnit && { parUnit: option.parUnit }),
        ...(option.parVal && { parVal: option.parVal })
      })) || [],
      QuotaReached: subscriptionPolicy.QuotaReached?.map(option => ({
        optionName: option.optionName,
        description: option.description,
        optionValue: option.optionValue,
        ...(option.parUnit && { parUnit: option.parUnit }),
        ...(option.parVal && { parVal: option.parVal })
      })) || [],
      NotificationIntimations: subscriptionPolicy.NotificationIntimations?.map(notification => ({
        percentage: notification.percentage,
        teams: notification.teams.map(team => {
          // Ensure we're using the correct ID from availableTeams
          const teamData = this.availableTeams.find((t: any) => (t.name || t.Name) === team.name);
          let teamId = '';
          if (teamData) {
            // Extract id which is an object with $oid property, or direct id string, or Id
            if (teamData.id && typeof teamData.id === 'object' && teamData.id.$oid) {
              teamId = teamData.id.$oid;
            } else if (teamData.id) {
              teamId = teamData.id;
            } else if (teamData.Id) {
              teamId = teamData.Id;
            }
          }
          // Fallback to the Id stored in the team object
          if (!teamId && team.Id) {
            teamId = team.Id;
          }
          this.logger.i(`Team: ${team.name}, ID: ${teamId}`);
          return {
            name: team.name,
            id: teamId || ""
          };
        })
      })) || []
    };
  }

  resetVMPolicy(): void {
    if (!this.resourcePolicy) return;
    this.resourcePolicy.VMPolicy = this.getDefaultVMPolicy();
    this.logger.i('VM Policy reset to defaults');
  }

  resetSubscriptionPolicy(): void {
    if (!this.resourcePolicy) return;
    this.resourcePolicy.SubscriptionPolicy = this.getDefaultSubscriptionPolicy();
    this.logger.i('Subscription Policy reset to defaults');
  }

  hasVMPolicy(): boolean {
    return !!this.resourcePolicy?.VMPolicy;
  }

  hasSubscriptionPolicy(): boolean {
    return !!this.resourcePolicy?.SubscriptionPolicy;
  }

  toggleSection(sectionKey: string): void {
    this.expandedSections[sectionKey] = !this.expandedSections[sectionKey];
  }

  isSectionExpanded(sectionKey: string): boolean {
    return this.expandedSections[sectionKey] || false;
  }
}
